import React, { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { apiGet, apiPost } from "../../shared/apiClient";
import { IconList, IconRefresh, IconSave } from "../../shared/toolbarIcons";

function toDateTimeInput(value) {
  if (!value) return "";
  const date = new Date(value);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

function toIsoOrNow(value) {
  if (!value) return new Date().toISOString();
  return new Date(value).toISOString();
}

const emptyForm = () => ({
  userId: "",
  description: "",
  date: "",
  isRead: false
});

export function NotificationsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const editParam = searchParams.get("edit");
  const hasEditParam = Boolean(editParam);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const loadLookups = async () => {
    setLoading(true);
    setError(null);
    const uRes = await apiGet("/api/Users");
    if (uRes.ok) setUsers(uRes.data);
    else setError(uRes.error ?? "Failed to load users");
    setLoading(false);
  };

  useEffect(() => {
    if (!hasEditParam) return;
    void loadLookups();
  }, [hasEditParam]);

  useEffect(() => {
    if (!editParam) {
      setEditingId(null);
      setForm(emptyForm());
      setLoading(false);
      return;
    }
    const id = Number(editParam);
    if (!Number.isFinite(id)) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      const res = await apiGet(`/api/Notifications/${id}`);
      if (cancelled) return;
      setLoading(false);
      if (!res.ok) {
        setError(res.error ?? "Failed to load notification");
        return;
      }
      const x = res.data;
      setEditingId(x.id);
      setForm({
        userId: x.userId ?? "",
        description: x.description ?? "",
        date: toDateTimeInput(x.date),
        isRead: Boolean(x.isRead)
      });
    })();
    return () => {
      cancelled = true;
      setLoading(false);
    };
  }, [editParam]);

  if (!hasEditParam) {
    return <Navigate to="/notifications-list" replace />;
  }

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm());
    setSearchParams({}, { replace: true });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.userId) return;
    setSaving(true);
    setError(null);
    const res = await apiPost("/api/Notifications", {
      id: editingId ?? 0,
      userId: Number(form.userId),
      description: form.description || null,
      date: toIsoOrNow(form.date),
      isRead: form.isRead
    });
    if (!res.ok) setError(res.error ?? "Failed to save notification");
    else {
      await loadLookups();
      resetForm();
    }
    setSaving(false);
  };

  const onRefresh = async () => {
    setError(null);
    await loadLookups();
    if (editParam) {
      const id = Number(editParam);
      if (!Number.isFinite(id)) return;
      setLoading(true);
      const res = await apiGet(`/api/Notifications/${id}`);
      setLoading(false);
      if (!res.ok) {
        setError(res.error ?? "Failed to load notification");
        return;
      }
      const x = res.data;
      setEditingId(x.id);
      setForm({
        userId: x.userId ?? "",
        description: x.description ?? "",
        date: toDateTimeInput(x.date),
        isRead: Boolean(x.isRead)
      });
    } else {
      setEditingId(null);
      setForm(emptyForm());
    }
  };

  return (
    <div className="card">
      <div className="card-header card-header--row">
        <div>
          <h2>Edit Notification</h2>
        </div>
        <div className="card-header-actions">
          <button
            className="icon-button icon-button--toolbar"
            type="submit"
            form="notifications-form"
            disabled={saving}
            title="Save"
            aria-label="Save notification"
          >
            <IconSave />
          </button>
          <button
            className="icon-button icon-button--toolbar"
            type="button"
            onClick={() => void onRefresh()}
            disabled={saving || loading}
            title="Refresh"
            aria-label="Refresh"
          >
            <IconRefresh />
          </button>
          <Link to="/notifications-list" className="icon-button icon-button--toolbar" title="Notifications list" aria-label="Open list">
            <IconList />
          </Link>
        </div>
      </div>
      <div className="card-body">
        <form id="notifications-form" onSubmit={onSubmit} className="form form-fields-bottomline">
          <div className="form-row form-row--3">
            <label className="field">
              <span>User</span>
              <select value={form.userId} onChange={(e) => setForm((p) => ({ ...p, userId: e.target.value ? Number(e.target.value) : "" }))}>
                <option value="">Select user</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.userName || `User #${u.id}`}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Date</span>
              <input type="datetime-local" value={form.date} onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))} />
            </label>
            <label className="field">
              <span>Description</span>
              <textarea rows={1} value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} />
            </label>
          </div>
          {error && <div className="error-banner">{error}</div>}
        </form>
      </div>
    </div>
  );
}
